import { PharmacistLayout } from "@/components/layout/pharmacist-layout";
import { Link } from "wouter";
import { TrendingUp, Award, Star, GraduationCap, Stethoscope, Users, ChevronRight } from "lucide-react";

const CAREER_CARDS = [
  {
    href: "/pharmacist/career/nhs-banding",
    icon: TrendingUp,
    color: "from-violet-500 to-violet-700",
    bg: "bg-violet-50",
    iconColor: "text-violet-600",
    title: "NHS Banding & Progression",
    desc: "Band 6 to Band 8c/8d consultant pharmacist — salary ranges, progression milestones, job planning (PAs), and how to build your career case.",
  },
  {
    href: "/pharmacist/career/rps-credentialing",
    icon: Award,
    color: "from-blue-500 to-blue-700",
    bg: "bg-blue-50",
    iconColor: "text-blue-600",
    title: "RPS Credentialing Pathway",
    desc: "Foundation to Advanced to Consultant — the RPS credentialing stages, APPF assessment, evidence portfolio requirements, and timeline expectations.",
  },
  {
    href: "/pharmacist/career/foundation-to-specialist",
    icon: Star,
    color: "from-emerald-500 to-emerald-700",
    bg: "bg-emerald-50",
    iconColor: "text-emerald-600",
    title: "Foundation to Specialist",
    desc: "How to move from foundation year into specialist rheumatology pharmacy — rotations, postgraduate qualifications, mentorship, and building your specialist CV.",
  },
  {
    href: "/pharmacist/career/academic",
    icon: GraduationCap,
    color: "from-amber-500 to-amber-700",
    bg: "bg-amber-50",
    iconColor: "text-amber-600",
    title: "Academic Pharmacy",
    desc: "PhD pathways, clinical academic roles, NIHR funding, and combining NHS practice with research in the academic rheumatology pharmacy space.",
  },
  {
    href: "/pharmacist/career/consultant",
    icon: Stethoscope,
    color: "from-rose-500 to-rose-700",
    bg: "bg-rose-50",
    iconColor: "text-rose-600",
    title: "Consultant Pharmacist Role",
    desc: "What the Band 8c/8d Consultant Pharmacist role involves — advanced clinical practice, leadership, service development, and the Consultant Pharmacist credential.",
  },
  {
    href: "/pharmacist/career/leadership",
    icon: Users,
    color: "from-teal-500 to-teal-700",
    bg: "bg-teal-50",
    iconColor: "text-teal-600",
    title: "Pharmacy Leadership",
    desc: "Chief Pharmacist pathway, NHS leadership programmes (GMTS, ELFH), strategic roles, and developing leadership capability alongside clinical practice.",
  },
];

export default function PharmacistCareerHub() {
  return (
    <PharmacistLayout title="Career Pathways">
      <div className="space-y-8">

        {/* Header */}
        <div className="bg-gradient-to-br from-violet-900 to-violet-950 rounded-2xl p-6 text-white">
          <div className="flex items-center gap-3 mb-3">
            <TrendingUp className="h-8 w-8 text-violet-300" />
            <div>
              <h2 className="text-xl font-bold">Pharmacy Career Pathways</h2>
              <p className="text-violet-300 text-sm">Rheumatology Specialist Pharmacist · NHS & Academic Routes</p>
            </div>
          </div>
          <p className="text-violet-200 text-sm leading-relaxed">
            Explore your career options as a UK rheumatology specialist pharmacist — from NHS band progression and RPS credentialing to academic pharmacy and senior leadership. Each pathway below provides a structured overview and practical guidance.
          </p>
        </div>

        {/* Career cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {CAREER_CARDS.map((card) => (
            <Link key={card.href} href={card.href}>
              <div className="bg-white rounded-2xl border border-slate-200 p-5 hover:shadow-md hover:border-violet-200 transition-all cursor-pointer group h-full">
                <div className={`${card.bg} ${card.iconColor} h-11 w-11 rounded-xl flex items-center justify-center mb-4 group-hover:scale-105 transition-transform`}>
                  <card.icon className="h-6 w-6" />
                </div>
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-bold text-slate-900 group-hover:text-violet-700 transition-colors leading-snug">{card.title}</h3>
                  <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-violet-500 shrink-0 mt-0.5 transition-colors" />
                </div>
                <p className="text-sm text-slate-500 leading-relaxed mt-2">{card.desc}</p>
              </div>
            </Link>
          ))}
        </div>

        {/* RPS Membership link */}
        <Link href="/pharmacist/career/rps-membership">
          <div className="bg-gradient-to-r from-violet-50 to-blue-50 border border-violet-200 rounded-2xl p-5 hover:shadow-md transition-all cursor-pointer group">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-xl bg-violet-100 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                <Award className="h-6 w-6 text-violet-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-slate-900 group-hover:text-violet-700 transition-colors">RPS Membership & Fellowship Guide</h3>
                <p className="text-sm text-slate-500 mt-0.5">MRPharmS, FRPharmS, RPS Faculty — requirements, costs, and application steps</p>
              </div>
              <ChevronRight className="h-5 w-5 text-slate-300 group-hover:text-violet-500 shrink-0 transition-colors" />
            </div>
          </div>
        </Link>

      </div>
    </PharmacistLayout>
  );
}
